import { useEffect } from 'react';
import type { RefObject } from 'react';

interface PreventScrollOptions {
  /** Whether the scroll lock is disabled. */
  isDisabled?: boolean;

  /**
   * Reference to the outermost element.
   * This is used to avoid scrollbars displacing content. */
  outerElementRef?: RefObject<HTMLElement>;
}

const scrollPaddingRight = '--scrollPaddingRight';

/**
 * Prevents scrolling on the document body on mount, and
 * restores it on unmount. Works great on mobile Safari.
 * Avoids content shifting due to scrollbars disappearing.
 */
export function usePreventScroll({
  isDisabled,
  outerElementRef,
}: PreventScrollOptions = {}) {
  useEffect(() => {
    if (isDisabled) {
      return;
    }

    const scrollingElement = document.scrollingElement;
    const offset = scrollingElement?.scrollTop ?? 0;
    const bodyScrollbarVisible =
      (scrollingElement?.scrollHeight ?? 0) >
      (scrollingElement?.clientHeight ?? 0);

    const outerElement = outerElementRef?.current ?? null;

    for (const el of [outerElement, document.body]) {
      if (el) {
        const current = window.getComputedStyle(el).paddingRight;
        const extra = `${
          window.innerWidth - document.documentElement.clientWidth
        }px`;
        const scrollbarVisible = el.scrollHeight > el.clientHeight;

        if (
          bodyScrollbarVisible &&
          ((el === outerElement && !scrollbarVisible) || el === document.body)
        ) {
          const value =
            current && current !== '0px'
              ? `calc(${current} + ${extra})`
              : extra;

          if (el === outerElement) {
            el.style.setProperty(scrollPaddingRight, value);
          } else {
            el.style.paddingRight = value;
          }
        }
      }
    }

    const style = document.body.style;
    style.top = offset * -1 + 'px';
    style.position = 'fixed';
    style.overflow = 'hidden';
    style.left = '0';
    style.right = '0';

    return () => {
      if (outerElement) {
        outerElement.style.removeProperty(scrollPaddingRight);
      }

      style.top = '';
      style.position = '';
      style.left = '';
      style.right = '';
      style.overflow = '';
      style.paddingRight = '';

      if (scrollingElement) {
        scrollingElement.scrollTop = offset;
      }
    };
  }, [isDisabled, outerElementRef]);
}
